/**
 * CoverProvider
 * Провайдер для работы с обложкой книги
 */

import { createContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import type { GalleryError, GalleryItem } from '@/types/gallery';
import { useBooks } from '@/books/useBooks';
import { useAuth } from '@/auth/useAuth';

type CoverAssetSide = 'front' | 'back';

interface CoverContextType {
  cover: GalleryItem | null;
  coverLoading: boolean;
  coverError: GalleryError | null;
  uploadProgress: number | null;
  uploadCover: (file: File, side?: CoverAssetSide) => Promise<GalleryItem | null>;
  deleteCover: (side?: CoverAssetSide) => Promise<void>;
  getCoverUrl: () => string | null;
  loadCover: () => Promise<void>;
}

export const CoverContext = createContext<CoverContextType | undefined>(undefined);

interface CoverProviderProps {
  children: ReactNode;
}

function getBackCoverPathPrefix(userId: string, bookId: string): string {
  return `${userId}/${bookId}/back/`;
}

function buildCoverFilePath(
  userId: string,
  bookId: string,
  fileName: string,
  side: CoverAssetSide
): string {
  if (side === 'back') {
    return `${getBackCoverPathPrefix(userId, bookId)}${fileName}`;
  }

  return `${userId}/${bookId}/${fileName}`;
}

export function CoverProvider({ children }: CoverProviderProps) {
  const { currentBook } = useBooks();
  const { user } = useAuth();
  const [cover, setCover] = useState<GalleryItem | null>(null);
  const [coverLoading, setCoverLoading] = useState(false);
  const [coverError, setCoverError] = useState<GalleryError | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const findCoverBySide = useCallback(
    async (side: CoverAssetSide): Promise<GalleryItem | null> => {
      if (!currentBook || !user) {
        return null;
      }

      const backPrefix = getBackCoverPathPrefix(user.id, currentBook.id);
      let query = supabase
        .from('gallery_items')
        .select('*')
        .eq('book_id', currentBook.id)
        .eq('user_id', user.id)
        .eq('kind', 'cover')
        .order('created_at', { ascending: false })
        .limit(1);

      query =
        side === 'back'
          ? query.like('file_path', `${backPrefix}%`)
          : query.not('file_path', 'like', `${backPrefix}%`);

      const { data, error } = await query.maybeSingle();

      if (error) {
        console.error('Cover load error:', error);
        throw error;
      }

      return data;
    },
    [currentBook, user]
  );

  const loadCover = useCallback(async () => {
    if (!currentBook || !user) {
      setCover(null);
      return;
    }

    setCoverLoading(true);
    setCoverError(null);

    try {
      const frontCover = await findCoverBySide('front');
      setCover(frontCover);
    } catch (error) {
      console.error('Cover load exception:', error);
      setCoverError({ message: 'Не удалось загрузить обложку.' });
      setCover(null);
    } finally {
      setCoverLoading(false);
    }
  }, [currentBook, findCoverBySide, user]);

  const uploadCover = useCallback(
    async (file: File, side: CoverAssetSide = 'front'): Promise<GalleryItem | null> => {
      if (!currentBook || !user) {
        setCoverError({ message: 'Книга или пользователь не выбраны.' });
        return null;
      }

      setCoverError(null);
      setUploadProgress(0);

      try {
        const existingCover =
          side === 'front' && cover ? cover : await findCoverBySide(side);

        if (existingCover) {
          await supabase.storage.from('covers').remove([existingCover.file_path]);
          await supabase.from('gallery_items').delete().eq('id', existingCover.id);
        }

        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = buildCoverFilePath(user.id, currentBook.id, fileName, side);

        const { error: uploadError } = await supabase.storage
          .from('covers')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          console.error('Cover upload error:', uploadError);
          setCoverError({ message: 'Не удалось загрузить изображение обложки.', code: uploadError.message });
          setUploadProgress(null);
          return null;
        }

        setUploadProgress(100);

        const newCover = {
          book_id: currentBook.id,
          user_id: user.id,
          title: side === 'back' ? `back:${file.name}` : file.name,
          file_path: filePath,
          file_url: null,
          mime_type: file.type,
          size_bytes: file.size,
          kind: 'cover' as const,
          created_at: new Date().toISOString(),
        };

        const { data, error: dbError } = await supabase
          .from('gallery_items')
          .insert([newCover])
          .select()
          .single();

        if (dbError) {
          console.error('Cover record create error:', dbError);
          await supabase.storage.from('covers').remove([filePath]);
          setCoverError({ message: 'Не удалось сохранить изображение обложки.', code: dbError.code });
          setUploadProgress(null);
          return null;
        }

        if (side === 'front') {
          setCover(data);
        }

        setUploadProgress(null);
        return data;
      } catch (error) {
        console.error('Cover upload exception:', error);
        setCoverError({ message: 'Произошла ошибка при загрузке изображения обложки.' });
        setUploadProgress(null);
        return null;
      }
    },
    [cover, currentBook, findCoverBySide, user]
  );

  const deleteCover = useCallback(
    async (side: CoverAssetSide = 'front') => {
      const targetCover =
        side === 'front' && cover ? cover : await findCoverBySide(side);

      if (!targetCover) {
        return;
      }

      try {
        const { error: storageError } = await supabase.storage
          .from('covers')
          .remove([targetCover.file_path]);

        if (storageError) {
          console.error('Cover file delete error:', storageError);
          setCoverError({ message: 'Не удалось удалить файл обложки.', code: storageError.message });
          return;
        }

        const { error: dbError } = await supabase
          .from('gallery_items')
          .delete()
          .eq('id', targetCover.id);

        if (dbError) {
          console.error('Cover record delete error:', dbError);
          setCoverError({ message: 'Не удалось удалить запись обложки.', code: dbError.code });
          return;
        }

        if (side === 'front') {
          setCover(null);
        }
      } catch (error) {
        console.error('Cover delete exception:', error);
        setCoverError({ message: 'Произошла ошибка при удалении обложки.' });
      }
    },
    [cover, findCoverBySide]
  );

  const getCoverUrl = (): string | null => {
    if (!cover) {
      return null;
    }

    const { data } = supabase.storage
      .from('covers')
      .getPublicUrl(cover.file_path);

    return data.publicUrl;
  };

  const currentBookId = currentBook?.id;
  const userId = user?.id;
  useEffect(() => {
    if (currentBookId && userId) {
      // loadCover — async-загрузка обложки из Supabase; синхронный setCoverLoading(true)
      // внутри неё и есть намеренный эффект, подавляем точечно.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      loadCover();
    } else {
      setCover(null);
    }
  }, [currentBookId, loadCover, userId]);

  const value = {
    cover,
    coverLoading,
    coverError,
    uploadProgress,
    uploadCover,
    deleteCover,
    getCoverUrl,
    loadCover,
  };

  return <CoverContext.Provider value={value}>{children}</CoverContext.Provider>;
}
